import Input from '../../../../../components/Input/Input';
import { phone as PHONE_MASK } from '../../../../../constants/mask';
import { textStyles } from '../../../../../constants/textStyles';
import { colors } from '../../../../../constants/colors';
import {
  CAR_FIELDS,
  FLOW_ECP,
  PERSON_FIELDS,
  REQUISITES_FIELDS,
  fieldDomId,
  isDcSeller,
  normalizePhone,
} from './form';
import { Fields, area, card, grid, hint, termsGrid, title } from './parts';

const DcRow = ({ label, value, bad }) => (
  <p style={{ ...hint, margin: '0 0 6px', color: bad ? colors.red : undefined }}>
    <b>{label}:</b> {value || '—'}
  </p>
);

// Продавец — дилерский центр: юрлицо из карточки ДЦ, за него подписывает директор.
const SellerDcCard = ({ seller, form, isError, setField }) => (
  <>
    <p style={hint}>Продавец — дилерский центр. Данные берутся из его карточки, подписывает директор.</p>
    <DcRow label="Наименование" value={seller.name} bad={isError('seller', 'name')} />
    <DcRow label="БИН" value={seller.bin} bad={isError('seller', 'bin')} />
    <DcRow label="Адрес" value={seller.address} bad={isError('seller', 'address')} />
    <DcRow label="В лице" value={seller.director} bad={isError('seller', 'director')} />
    <DcRow label="На основании" value={seller.basis} />
    {isError('seller', 'signer') && (
      <p style={{ ...hint, color: colors.red }}>
        У дилерского центра нет директора (роль «Директор ДЦ»): подписывать договор некому. Заведите его в админке.
      </p>
    )}
    <div style={{ ...grid, marginTop: 12 }}>
      <Fields
        fields={PERSON_FIELDS.filter((field) => field.key === 'phone')}
        section="seller"
        form={form}
        isError={isError}
        onChange={setField}
      />
    </div>
  </>
);

const ContractForm = ({ form, state, flow, isError, setField, setForm }) => (
  <>
    <div style={card}>
      <p style={title}>Агент</p>
      <p style={hint}>
        {state?.values?.agent?.name || 'ДЦ'}
        {state?.values?.agent?.bin ? `, БИН ${state.values.agent.bin}` : ''}
      </p>
      <div style={grid}>
        <Input
          id={fieldDomId('agent.director')}
          label="В лице"
          placeholder="Кто подписывает за площадку"
          value={form.agent.director}
          error={isError('agent', 'director')}
          handleSetState={(value) => setField('agent', 'director', value)}
        />
        <Input
          id={fieldDomId('agent.basis')}
          label="На основании"
          placeholder="Устава"
          value={form.agent.basis}
          error={isError('agent', 'basis')}
          handleSetState={(value) => setField('agent', 'basis', value)}
        />
        {flow === FLOW_ECP && (
          <Input
            id={fieldDomId('agent.phone')}
            label="Телефон директора"
            placeholder="+7 (___) ___-__-__"
            mask={PHONE_MASK}
            value={form.agent.phone}
            error={isError('agent', 'phone')}
            handleSetState={(value) => setField('agent', 'phone', normalizePhone(value))}
          />
        )}
      </div>
    </div>

    <div style={card}>
      <p style={title}>Продавец</p>
      {isDcSeller(state?.values) ? (
        <SellerDcCard seller={state.values.seller} form={form} isError={isError} setField={setField} />
      ) : (
        <div style={grid}>
          <Fields fields={PERSON_FIELDS} section="seller" form={form} isError={isError} onChange={setField} />
        </div>
      )}
    </div>

    <div style={card}>
      <p style={title}>Покупатель</p>
      <div style={grid}>
        <Fields fields={PERSON_FIELDS} section="buyer" form={form} isError={isError} onChange={setField} />
      </div>
    </div>

    <div style={card}>
      <p style={title}>Автомобиль</p>
      <div style={grid}>
        <Fields fields={CAR_FIELDS} section="car" form={form} isError={isError} onChange={setField} />
      </div>
    </div>

    <div style={card}>
      <p style={title}>Счёт продавца</p>
      <div style={grid}>
        <Input
          id={fieldDomId('price')}
          label="Стоимость автомобиля, ₸"
          value={form.price || ''}
          error={isError('price')}
          handleSetState={(value) => setForm((prev) => ({ ...prev, price: value.replace(/[^\d]/g, '') }))}
        />
        <Fields fields={REQUISITES_FIELDS} section="requisites" form={form} isError={isError} onChange={setField} />
      </div>
    </div>

    <div style={card}>
      <p style={title}>Сроки</p>
      <div style={termsGrid}>
        <Input
          id={fieldDomId('terms.agentFee')}
          label="Вознаграждение агента, ₸ — удерживается из выплаты продавцу"
          value={form.terms.agentFee}
          error={isError('terms', 'agentFee')}
          handleSetState={(value) => setField('terms', 'agentFee', value.replace(/[^\d]/g, ''))}
        />
        <Input
          label="Срок перевода денег продавцу — банковских дней после поступления полной оплаты на счёт площадки"
          value={form.terms.payoutDays}
          handleSetState={(value) => setField('terms', 'payoutDays', value.replace(/[^\d]/g, ''))}
        />
        <Input
          label="Срок передачи автомобиля покупателю — календарных дней после поступления полной оплаты на счёт площадки"
          value={form.terms.handoverDays}
          handleSetState={(value) => setField('terms', 'handoverDays', value.replace(/[^\d]/g, ''))}
        />
      </div>
      <label style={{ display: 'block', marginTop: 16 }}>
        <p style={{ ...textStyles.fieldLabel, marginBottom: 8 }}>Дефекты авто</p>
        <textarea
          value={form.terms.defects}
          placeholder="Пусто — в договоре будет «недостатков не выявлено»"
          onChange={(e) => setField('terms', 'defects', e.target.value)}
          rows={3}
          style={area}
        />
      </label>
    </div>

    <div style={card}>
      <p style={title}>При передаче авто</p>
      <div style={termsGrid}>
        <Input
          label="Ключи от автомобиля, шт."
          placeholder="Сколько комплектов передают вместе с авто"
          value={form.terms.keysCount}
          handleSetState={(value) => setField('terms', 'keysCount', value.replace(/[^\d]/g, ''))}
        />
      </div>
      <label style={{ display: 'block', marginTop: 16 }}>
        <p style={{ ...textStyles.fieldLabel, marginBottom: 8 }}>Дополнительное оборудование / комплектация</p>
        <textarea
          value={form.terms.extraEquipment}
          placeholder="Пусто — в акте останется прочерк"
          onChange={(e) => setField('terms', 'extraEquipment', e.target.value)}
          rows={2}
          style={area}
        />
      </label>
    </div>
  </>
);

export default ContractForm;
